import { Box, Typography, useTheme } from '@mui/material';
import { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { Banner, BannerType, bannerTemplateKeys } from '../../models/BannersModels';
import CloseIcon from '../Icons/CloseIcon';
import ClickableLink from '../Text/ClickableLink';

interface NotificationBannerProps {
  banner: Banner;
}

function NotificationBanner({ banner }: NotificationBannerProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const dismissedKey = `DISMISSED_BANNER_${banner.id}`;
  const [isVisible, setIsVisible] = useState(sessionStorage.getItem(dismissedKey) !== 'true');

  /**
   * Dismisses the banner for the rest of this browser session only, not permanently
   */
  const handleClose = () => {
    sessionStorage.setItem(dismissedKey, 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const backgroundByType: Record<BannerType, string> = {
    [BannerType.PLANNED_CHANGE]: theme.palette.banner.plannedChange,
    [BannerType.OUTAGE]: theme.palette.banner.outage,
    [BannerType.OTHER]: theme.palette.banner.other,
  };

  const templateKey = bannerTemplateKeys[banner.bannerType as BannerType];

  /**
   * Formats an ISO date string for display, e.g. "13 July 2026, 09:00"
   * @param iso
   */
  const formatDateTime = (iso: string) =>
    iso ? DateTime.fromISO(iso).setLocale(i18n.language).toFormat('d MMMM yyyy, HH:mm') : '';

  const templateStartDate = banner.maintenanceStart || banner.startDate;
  const templateEndDate = banner.maintenanceEnd || banner.endDate;

  return (
    <Box
      sx={{
        position: 'relative',
        padding: '1rem 3rem',
        backgroundColor: backgroundByType[banner.bannerType as BannerType],
        textAlign: 'center',
        marginBottom: '0.125rem',
      }}
    >
      <Typography sx={{ lineHeight: '1.625rem', color: theme.palette.monochrome.monoChrome1 }}>
        {templateKey ? (
          <Trans
            i18nKey={`pages.bannerOnboarding.message.templates.${templateKey}`}
            values={{
              startDate: formatDateTime(templateStartDate),
              endDate: formatDateTime(templateEndDate),
            }}
            tOptions={{ interpolation: { escapeValue: false } }}
            components={{
              Link: (
                <ClickableLink
                  to="https://portal.vodafone.com/web/portal/tickets"
                  target="_blank"
                  sx={{
                    color: theme.palette.monochrome.monoChrome1,
                    textDecorationColor: theme.palette.monochrome.monoChrome1,
                  }}
                />
              ),
            }}
          />
        ) : (
          banner.messages[i18n.language] || banner.messages.en
        )}
      </Typography>
      {banner.dismissible ? (
        <Box sx={{ position: 'absolute', right: '1.5rem', top: '50%', transform: 'translateY(-50%)' }}>
          <CloseIcon cursor="pointer" onClick={handleClose} stroke={theme.palette.monochrome.monoChrome1} />
        </Box>
      ) : null}
    </Box>
  );
}

export default memo(NotificationBanner);
